import { defineStore } from 'pinia';
import { computed, shallowRef } from 'vue';
import { Data, companiesService } from '@/connect';
import { PbCompanyInfo } from '@/connect/gen/common/protos/types_pb';
import { useSnackbarManager } from '@/modules/snackbar/services/snackbar-manager';
import i18n from '@/localisation/i18n';

export const useCompaniesStore = defineStore('companies', () => {
  const snackbarManager = useSnackbarManager();

  // Once we migrate to Vue3 we should use shallowReactive
  const companies = shallowRef(new Map<string, Data<PbCompanyInfo>>());

  async function refreshCompanies() {
    const result = await companiesService.queryCompanies();

    if (!result.success) {
      snackbarManager.error(i18n.t(result.error, result.details) as string);
      return;
    }

    addCompaniesFromList(result.data.companies);
  }

  function addCompanies(items: Record<string, Data<PbCompanyInfo>>) {
    addCompaniesFromList(Object.values(items));
  }

  function addCompaniesFromList(items: Iterable<Data<PbCompanyInfo>>) {
    const companiesNew = new Map(companies.value);
    for (const company of items) {
      companiesNew.set(company.displayId, company);
    }
    companies.value = companiesNew;
  }

  function getCompany(id: string) {
    return companies.value.get(id);
  }

  return {
    addCompanies,
    getCompany,
    addCompaniesFromList,
    refreshCompanies,
    companies: computed(() => [...companies.value.values()]),
  };
});

export type CompaniesStore = ReturnType<typeof useCompaniesStore>;
